import { useEffect, useState } from 'react';
import { createConsentSubmission, getConsent, getMe } from '../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../api/hooks.js';
import {
  Async,
  Badge,
  Button,
  Failure,
  Field,
  KeyValue,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

/**
 * POST /v1/consent/submissions takes `{ subject_person_id }` and nothing else —
 * no signer name, because a typed-in name on a consent form is not a signature.
 *
 * The returned signing_url is a BEARER CAPABILITY: whoever holds it can sign in
 * the subject's name. It is opened in a new tab and never stored, never put in a
 * route, and never logged — index.html also sets referrer=no-referrer so it
 * cannot leak through a Referer header.
 */
export function ConsentScreen() {
  const me = useApi(getMe, []);
  const [subjectId, setSubjectId] = useState('');
  const [lookupId, setLookupId] = useState('');
  const intent = useIntentKey();

  // Self-consent is the journey's default — prefill it, don't make the user
  // copy their own person_id out of the eligibility panel.
  const myPersonId = me.data?.person.person_id;
  useEffect(() => {
    if (myPersonId === undefined) return;
    setSubjectId((current) => (current === '' ? myPersonId : current));
  }, [myPersonId]);

  // Tracking starts itself on creation, and the panel polls: signing happens
  // in another tab and lands here via DocuSeal's webhook, so this screen flips
  // to signed on its own (the enrolment screen's pattern).
  const create = useAction(createConsentSubmission, (result) => setLookupId(result.consent_id));
  const status = useApi(() => getConsent(lookupId), [lookupId], {
    skip: lookupId.length === 0,
    pollMs: 3000,
  });

  return (
    <Screen
      title="Consent"
      lede="DocuSeal is live on dev — and LiveDocuSealGateway has never actually run, so the first submission here is also its first real request."
    >
      <Async state={me}>
        {(data) => (
          <Panel title="eligibility">
            <KeyValue
              rows={[
                ['your person_id', <code key="p">{data.person.person_id}</code>],
                ['required_signer_role', data.consent_eligibility.required_signer_role],
                ['blocked_reason', data.consent_eligibility.blocked_reason],
                ['consent_signed', <Badge key="c">{String(data.onboarding.consent_signed)}</Badge>],
              ]}
            />
            <Row>
              <Button
                variant="secondary"
                onClick={() => setSubjectId(data.person.person_id)}
              >
                Use my person_id as the subject
              </Button>
            </Row>
          </Panel>
        )}
      </Async>

      <Panel title="create a submission">
        <Row>
          <Field label="subject_person_id" value={subjectId} onChange={setSubjectId} />
          <Button
            onClick={() => create.run(subjectId, intent.key)}
            disabled={create.pending || subjectId.length === 0}
          >
            {create.pending ? 'creating…' : 'Create submission'}
          </Button>
        </Row>
        <p className="muted" style={{ marginTop: 8 }}>
          Idempotency-Key: <code>{intent.key}</code> — a replay re-derives the signing URL from the
          stored submitter slug rather than returning a URL that was kept at rest.
        </p>
        {create.error !== null && (
          <Failure
            error={create.error}
            hint={
              {
                DOB_REQUIRED: 'The subject has no date of birth yet — it decides who must sign.',
                UNDER_MIN_AGE: 'Below the enrolment floor (MIN_ENROLMENT_AGE), which is not the discovery floor.',
                GUARDIANSHIP_REQUIRED: 'A minor subject needs a guardian signer.',
                GUARDIANSHIP_UNAVAILABLE: 'No eligible guardian exists in this household.',
                SUBJECT_EMAIL_UNVERIFIED:
                  'The subject’s email must be verified first — and EMAIL_PROVIDER=fake on dev means that mail cannot arrive.',
              }[create.error.code]
            }
          />
        )}
        {create.result !== null && (
          <>
            <KeyValue
              rows={[
                ['consent_id', <code key="c">{create.result.consent_id}</code>],
                ['document_version', create.result.document_version],
                ['expires_at', create.result.expires_at],
              ]}
            />
            <Row>
              <a
                className="action"
                style={{ textDecoration: 'none' }}
                href={create.result.signing_url}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open signing page →
              </a>
            </Row>
            <Note>
              Sign in the tab that opens, then come back — the panel below is already tracking this
              consent and flips on its own. The signing URL is deliberately not shown as text and
              not stored anywhere: it is a capability, not an identifier.
            </Note>
          </>
        )}
      </Panel>

      <Panel title="track a submission">
        <Row>
          <Field label="consent_id" value={lookupId} onChange={setLookupId} />
        </Row>
        {lookupId.length > 0 && (
          <Async state={status}>
            {(data) => (
              <>
                <KeyValue
                  rows={[
                    ['status', <Badge key="s">{data.status}</Badge>],
                    ['signer_role', data.signer_role],
                    ['document_version', data.document_version],
                    ['signed_at', data.signed_at],
                    ['expires_at', data.expires_at],
                  ]}
                />
                <Raw value={data} />
              </>
            )}
          </Async>
        )}
        <Note>
          Signing completes through DocuSeal’s webhook to the backend, not through this app. This
          panel polls every 3 seconds, so a signature appears here by itself; if it stalls, the
          attempt is recorded in <code>shared.service_calls</code>.
        </Note>
      </Panel>
    </Screen>
  );
}
